// AF-3.20 Phase 1 — Real Brand Onboarding.
// Final hardening phase — Company Discovery wiring.
//
// ROOT CAUSE (found by live product-journey audit, not inferred from
// code): this page's own submit handler only ever called POST /clients.
// The real, already-working AI research pipeline
// (POST /brand/discovery/start -> company_discovery.py -> the exact
// same CompanyContextService.set_inferred_context every other Company
// Context write goes through) was wired ONLY into BrandDiscoveryPage.js
// (public/discovery), which itself is only reached when a visitor's
// website survives from the landing page's own domain-capture hero
// through LoginPage.js's handleRegisterWithPersona (?url= present).
// Any signup that reaches this page instead (persona picker's own
// default "Add a Brand" destination -- the actual majority path,
// confirmed by walking the real signup flow start to finish) never
// touched discovery at all, so Company Context stayed permanently
// empty. Fixed here by having this form's OWN submit call the SAME
// real discovery endpoint immediately after creating the client --
// not a second pipeline, not a fabricated result.
//
// Why this is safe to bolt on rather than needing a backend change:
// POST /brand/discovery/start looks up an existing client by the exact
// (user_id, normalized website) pair before ever creating one, and
// reuses it when found (verified by direct read of server.py's
// start_brand_discovery). So calling it with the SAME normalized
// website this form already sent to POST /clients makes it discover
// INTO the client this page just created, never a duplicate. The one
// real risk that would break that reuse -- this form's website field
// not being normalized the same way normalize_website() normalizes it
// server-side (protocol/trailing-slash/case) -- is closed by running
// the exact same looksLikeDomain() normalizer BrandDiscoveryPage.js
// already uses (imported from there, not re-implemented) before either
// call.
//
// CRITICAL, still true and unchanged from the original version of this
// file: the real ClientCreate model (server.py) accepts exactly `name`,
// `website`, `industry`, `monthly_budget`, `status`. POST /clients also
// still injects random.randint/random.uniform seo_score/ad_spend/roas/
// organic_traffic/pages_indexed on every new client -- this page still
// never reads or displays any of those five fields.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { clientsAPI, brandDiscoveryAPI, companyContextAPI } from '@/lib/api';
import { CheckCircle, Warning, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { looksLikeDomain } from '@/pages/public/BrandDiscoveryPage';
import { FIELD_META, LIST_FIELD_META, EditableTextField, EditableListField } from '@/pages/brand/CompanyContextPage';
import { saveErrorMessage } from '@/pages/brand/CompanyContextPage';

const INDUSTRY_OPTIONS = ['Health & Wellness', 'Beauty & Personal Care', 'Fashion & Apparel', 'Food & Beverage', 'Home & Lifestyle', 'Technology', 'Other'];

// Same honest, non-fabricated narrative BrandDiscoveryPage.js already
// uses for the one real research call -- no step here is ever marked
// done until the real response returns and genuinely contains evidence
// for it (see the review stage below, driven by real discovery.fields_found).
const RESEARCH_STEPS = ['Researching your website…', 'Analyzing your company…', 'Building your Company Context…'];
const STEP_EVIDENCE_FIELDS = [null, ['business_summary', 'audience', 'positioning'], ['markets_served', 'growth_priorities', 'primary_market']];

export default function BrandOnboardingPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [website, setWebsite] = useState(() => new URLSearchParams(window.location.search).get('domain') || '');
  const [industry, setIndustry] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // form -> discovering -> review, or discovery-failed (client still
  // exists either way once we're past 'form' -- see clientRef below).
  const [stage, setStage] = useState('form');
  const [clientInfo, setClientInfo] = useState(null); // { id, name, website, industry }
  const [discoveryError, setDiscoveryError] = useState('');
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [result, setResult] = useState(null); // { client, company_context, discovery }

  const runDiscovery = async (normalizedWebsite) => {
    setStage('discovering');
    setDiscoveryError('');
    setActiveStepIndex(0);
    const stepTimer = setInterval(() => {
      setActiveStepIndex((i) => (i + 1 < RESEARCH_STEPS.length ? i + 1 : i));
    }, 1400);
    try {
      const res = await brandDiscoveryAPI.start(normalizedWebsite);
      setResult(res.data);
      setStage('review');
    } catch (err) {
      // Section 5 (failure handling): the client from POST /clients
      // already exists and is untouched -- this is never lost. Only the
      // discovery step honestly reports that it didn't complete.
      setDiscoveryError(err?.response?.data?.detail || "Martian couldn't finish researching your website.");
      setStage('discovery-failed');
    } finally {
      clearInterval(stepTimer);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !website.trim() || !industry) {
      toast.error('Brand name, website, and business category are required');
      return;
    }
    const normalizedWebsite = looksLikeDomain(website);
    if (!normalizedWebsite) {
      toast.error('Enter a valid website, like acme.com');
      return;
    }
    setSubmitting(true);
    try {
      const res = await clientsAPI.create({ name: name.trim(), website: normalizedWebsite, industry, status: 'active' });
      // Only the fields the user actually entered are echoed back --
      // res.data also carries backend-injected seo_score/ad_spend/roas/
      // organic_traffic/pages_indexed (see file header); none of those
      // are read here.
      setClientInfo({ id: res.data.id, name: res.data.name, website: res.data.website, industry: res.data.industry });
      toast.success('Brand workspace created');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create brand workspace');
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    // The real discovery pipeline reuses this exact client (see file
    // header) -- this is not a second, parallel creation.
    await runDiscovery(normalizedWebsite);
  };

  const retryDiscovery = () => runDiscovery(clientInfo.website);

  // Returns false on failure so the shared editor keeps the founder's draft open.
  const saveField = async (key, value) => {
    try {
      const res = await companyContextAPI.update(result.client.id, { [key]: value });
      setResult((r) => ({ ...r, company_context: res.data }));
      return true;
    } catch (err) {
      toast.error(saveErrorMessage(err));
      return false;
    }
  };

  const fieldsFound = result?.discovery?.fields_found || [];
  const pagesChecked = result?.discovery?.pages_checked || 0;

  return (
    <DashboardLayout>
      <div data-testid="brand-onboarding-page" className="max-w-lg mx-auto">
        {stage === 'form' && (
          <>
            <div className="mb-6">
              <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Understand your brand</h1>
              <p className="text-[12px] text-[var(--text-secondary)]">
                Martian needs a few real details to start tracking how AI, search, and your commerce/paid channels discover you.
                No account credentials or passwords are ever collected here.
              </p>
            </div>
            <form onSubmit={submit} className="space-y-4 rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-5 shadow-[var(--shadow-card)]" data-testid="brand-onboarding-form">
              <div className="space-y-1.5">
                <Label htmlFor="brand-name">Brand name</Label>
                <Input id="brand-name" data-testid="onboarding-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Wellness" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="brand-website">Website / domain</Label>
                <Input id="brand-website" data-testid="onboarding-website-input" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="acme.com" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="brand-industry">Business category</Label>
                <Select value={industry} onValueChange={setIndustry}>
                  <SelectTrigger id="brand-industry" data-testid="onboarding-industry-select"><SelectValue placeholder="Select a category" /></SelectTrigger>
                  <SelectContent>
                    {INDUSTRY_OPTIONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-[10px] text-[var(--text-tertiary)] italic" data-testid="onboarding-fields-note">
                After you continue, Martian will research your website and fill in primary market, audience, positioning, and more —
                you'll be able to review and correct everything before it's final.
              </p>
              <Button type="submit" data-testid="onboarding-submit-button" disabled={submitting} className="w-full">
                {submitting ? 'Creating…' : 'Create brand workspace'}
              </Button>
            </form>
          </>
        )}

        {stage === 'discovering' && (
          <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-6 shadow-[var(--shadow-card)]" data-testid="brand-onboarding-discovering">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle size={18} className="text-[var(--success)]" weight="fill" />
              <p className="text-[13px] text-[var(--text-primary)] font-semibold">{clientInfo?.name} workspace created</p>
            </div>
            <h2 className="font-display text-[17px] font-bold text-[var(--text-primary)] mb-1">Let's understand your business</h2>
            <p className="text-[12px] text-[var(--text-secondary)] mb-5">Martian is researching your company, market, and competitive landscape. This can take up to a minute.</p>
            <ul className="space-y-2.5" data-testid="brand-onboarding-progress">
              {RESEARCH_STEPS.map((label, i) => (
                <li key={label} data-testid={`brand-onboarding-progress-step${i === activeStepIndex ? '-active' : ''}`} className="flex items-center gap-2.5 text-[13px]">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${i === activeStepIndex ? 'bg-[var(--accent)] animate-pulse' : i < activeStepIndex ? 'bg-[var(--success)]' : 'bg-[var(--border)]'}`} aria-hidden="true" />
                  <span className={i === activeStepIndex ? 'text-[var(--text-primary)] font-medium' : 'text-[var(--text-tertiary)]'}>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {stage === 'discovery-failed' && (
          <div className="rounded-[var(--radius-card)] border border-[var(--warning-border)] bg-[var(--warning-bg)] p-6" data-testid="brand-onboarding-discovery-failed">
            <div className="flex items-center gap-2 mb-3">
              <Warning size={20} className="text-[var(--warning)]" weight="fill" />
              <p className="text-[14px] text-[var(--text-primary)] font-semibold">We couldn't complete Company Discovery yet</p>
            </div>
            <p className="text-[12px] text-[var(--text-secondary)] mb-1">{discoveryError}</p>
            <p className="text-[11px] text-[var(--text-tertiary)] mb-5">
              Your <strong>{clientInfo?.name}</strong> workspace is real and saved — nothing was lost. You can retry research now, or continue
              and fill in Company Context yourself later.
            </p>
            <div className="flex gap-2">
              <Button data-testid="brand-onboarding-retry-discovery" onClick={() => runDiscovery(clientInfo.website)} className="flex-1">Retry discovery</Button>
              <Button data-testid="brand-onboarding-skip-discovery" variant="outline" onClick={() => navigate('/growth/data-connections')} className="flex-1">Skip for now →</Button>
            </div>
          </div>
        )}

        {stage === 'review' && result && (
          <div data-testid="brand-onboarding-review">
            <div className="mb-5">
              <h1 className="font-display text-[19px] font-bold text-[var(--text-primary)] mb-1">Here's your starting Company Context</h1>
              <p className="text-[12px] text-[var(--text-secondary)]">Edit anything that's not quite right — nothing here is final until you say so.</p>
            </div>
            <ul className="mb-5 space-y-1.5" data-testid="brand-onboarding-found-summary">
              <li className="flex items-center gap-2 text-[12px] text-[var(--text-secondary)]" data-testid="brand-onboarding-found-website">
                <Check size={13} weight="bold" className={pagesChecked > 0 ? 'text-[var(--success)]' : 'text-[var(--text-tertiary)]'} />
                {pagesChecked > 0 ? `Checked ${pagesChecked} page${pagesChecked === 1 ? '' : 's'} of your website` : "Couldn't reach your website"}
              </li>
              {RESEARCH_STEPS.slice(1).map((label, idx) => {
                const evidenceFields = STEP_EVIDENCE_FIELDS[idx + 1] || [];
                const found = evidenceFields.some((f) => fieldsFound.includes(f));
                return (
                  <li key={label} className="flex items-center gap-2 text-[12px] text-[var(--text-secondary)]" data-testid={`brand-onboarding-found-${idx}`}>
                    <Check size={13} weight="bold" className={found ? 'text-[var(--success)]' : 'text-[var(--border-strong,var(--border))]'} />
                    {found ? label.replace('…', '') : `${label.replace('…', '')} — not enough public information`}
                  </li>
                );
              })}
            </ul>
            <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-card)] px-5" data-testid="brand-onboarding-context-form">
              <EditableTextField meta={FIELD_META[0]} field={result.company_context.business_summary} onSave={saveField} />
              <EditableTextField meta={FIELD_META[1]} field={result.company_context.primary_market} onSave={saveField} />
              <EditableListField meta={LIST_FIELD_META[0]} field={result.company_context.markets_served} onSave={saveField} />
              <EditableTextField meta={FIELD_META[2]} field={result.company_context.audience} onSave={saveField} />
              <EditableTextField meta={FIELD_META[3]} field={result.company_context.positioning} onSave={saveField} />
              <EditableListField meta={LIST_FIELD_META[1]} field={result.company_context.growth_priorities} onSave={saveField} />
              <EditableTextField meta={FIELD_META[4]} field={result.company_context.additional_context} onSave={saveField} />
            </div>
            <Button data-testid="brand-onboarding-continue" onClick={() => navigate('/growth/data-connections')} className="w-full mt-5">
              Connect your data →
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
